import { cn } from '@frontend-platform/ui-lib';
import { ChevronRight, MoveRight, Undo2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { BandBadge } from './band-badge';
import type { RunDetail } from './types';

interface CriteriaPanelProps {
  runDetails: RunDetail | undefined;
  selectedCategory: string | null;
  selectedMetric: string | null;
  onSelectCategory: (category: string) => void;
  onSelectMetric: (metricId: string) => void;
  onClear: () => void;
}

interface CriteriaRow {
  rowKey: string;
  isCategory: boolean;
  category: string;
  metricId?: string;
  name: string;
  band?: string;
  recoCount: number;
}

// Left panel: category → metric tree with health band + recommendation count.
// Ported from aistudio's CriteriaPanel.vue. Categories are expandable; clicking
// a category selects the category view, clicking a metric selects that metric.
export function CriteriaPanel({
  runDetails,
  selectedCategory,
  selectedMetric,
  onSelectCategory,
  onSelectMetric,
  onClear,
}: CriteriaPanelProps) {
  // A category is expanded unless explicitly collapsed (default-open), matching
  // the Vue `expandedCategories[cat] !== false` semantics.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const isExpanded = (category: string) => !collapsed[category];
  const toggleCategory = (category: string) =>
    setCollapsed((prev) => ({ ...prev, [category]: isExpanded(category) }));

  const rows = useMemo<CriteriaRow[]>(() => {
    const categories = runDetails?.overallEval?.categorySummaries ?? [];
    const metrics = runDetails?.metricSummaries ?? [];
    const items = runDetails?.items ?? [];

    return categories.flatMap((cat) => {
      const children: CriteriaRow[] = metrics
        .filter((m) => m.category === cat.category)
        .map((m) => ({
          rowKey: `metric-${m.metricId}`,
          isCategory: false,
          category: m.category,
          metricId: m.metricId,
          name: m.metricName,
          band: m.band,
          recoCount: m.recoCount ?? 0,
        }));

      const metricIds = children.map((c) => c.metricId);
      const categoryRecoCount = items.filter((item) =>
        item.contributingMetrics?.some((id) => metricIds.includes(id))
      ).length;

      const categoryRow: CriteriaRow = {
        rowKey: `category-${cat.category}`,
        isCategory: true,
        category: cat.category,
        name: cat.categoryLabel,
        band: cat.band,
        recoCount: categoryRecoCount,
      };

      // Inline the expansion check so the memo's deps are explicit (collapsed).
      const expanded = !collapsed[cat.category];
      return expanded ? [categoryRow, ...children] : [categoryRow];
    });
  }, [runDetails, collapsed]);

  const hasSelection = !!selectedCategory || !!selectedMetric;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background">
      <div className="flex h-11 items-center justify-between border-border border-b px-6">
        <span className="font-semibold text-base text-foreground">Criteria</span>
        {hasSelection && (
          <button
            type="button"
            onClick={onClear}
            className="flex cursor-pointer items-center gap-1 font-medium text-foreground text-xs hover:text-foreground"
          >
            <Undo2 className="h-3 w-3" />
            Clear
          </button>
        )}
      </div>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-border border-b bg-muted text-left text-foreground text-sm">
            <th className="px-6 py-3 font-semibold">Criteria</th>
            <th className="px-2 py-3 font-semibold">Health</th>
            <th className="px-4 py-3 text-right font-semibold">Recommendations</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isActive = row.isCategory
              ? selectedCategory === row.category
              : selectedMetric === row.metricId;
            return (
              <tr
                key={row.rowKey}
                onClick={() =>
                  row.isCategory
                    ? onSelectCategory(row.category)
                    : row.metricId && onSelectMetric(row.metricId)
                }
                className={cn(
                  'cursor-pointer border-border border-b last:border-b-0 hover:bg-muted',
                  isActive && 'bg-muted'
                )}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1.5">
                    {row.isCategory ? (
                      <ChevronRight
                        className={cn(
                          'h-4 w-4 shrink-0 text-foreground transition-transform',
                          isExpanded(row.category) && 'rotate-90'
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCategory(row.category);
                        }}
                      />
                    ) : (
                      <span className="ml-4 inline-block w-2.5" />
                    )}
                    <span
                      className={cn(
                        'text-foreground',
                        row.isCategory ? 'font-semibold' : 'font-normal'
                      )}
                    >
                      {row.name}
                    </span>
                  </div>
                </td>
                <td className="px-2 py-4">
                  <BandBadge band={row.band} />
                </td>
                <td className="px-4 py-4 text-right">
                  <span className="inline-flex items-center gap-3">
                    <span className={row.isCategory ? 'font-semibold' : 'font-normal'}>
                      {row.recoCount}
                    </span>
                    <MoveRight className="h-4 w-4 text-foreground" />
                  </span>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} className="px-6 py-10 text-center text-muted-foreground text-sm">
                No criteria available for this run.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
